import { useState, useEffect,useCallback } from "react";
import {
  Drawer,
  Box,
  Typography,
  Container,
  Button,
  TextField,
  Card,
  CardContent,
  Snackbar,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  createTheme,
  ThemeProvider,
  Paper,
  Stack,
  Chip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import ChatIcon from "@mui/icons-material/Chat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import GetUserFiles from "./GetUserFiles.jsx";
import BoxGroup from "./BoxGroup.jsx";
import { useCookies } from "react-cookie";
import axios from "axios";
import "../styles/customdrawer.css";
import config from "../config.js";
import { useSelector } from "react-redux";

// Professional Custom Theme
const customTheme = createTheme({
  palette: {
    primary: {
      main: "#EB5A3C",
      light: "#FF7F5F",
      dark: "#D63F1E",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#F8EEDB",
      light: "#FFF5E6",
      dark: "#E6D5B8",
      contrastText: "#2C2C2C",
    },
    background: {
      default: "#FAFAFA",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#2C2C2C",
      secondary: "#666666",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
      letterSpacing: "0.5px",
    },
    body2: {
      lineHeight: 1.5,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          border: "1px solid #E0E0E0",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 8,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
          },
        },
      },
    },
  },
});

const CustomDrawer = ({ isDrawerOpen, handleDrawerToggle, refreshFiles }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Existing state
  const [newBalanceSheetResult, setNewBalanceSheetResult] = useState("");

  const [cookies] = useCookies(["token", "userId"]);
  const [inputValue, setInputValue] = useState("");
  const [messages, setMessages] = useState([]);
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const userId = useSelector((state) => state.auth.user?.id);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // New state for financial reports
  const [financialReports, setFinancialReports] = useState([]);
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [analysisData, setAnalysisData] = useState({
    outliner: "",
    Highlights: "",
    RYG: "",
    Actions: "",
    Trends: "",
    Projection: newBalanceSheetResult,
  });

  const { API_BASE_URL } = config;

  // Responsive drawer width
  const getDrawerWidth = () => {
    if (isMobile) return "100vw";
    return Math.min(480, window.innerWidth * 0.42);
  };

  // Update analysisData when newBalanceSheetResult changes
  useEffect(() => {
    setAnalysisData((prev) => ({
      ...prev,
      Projection: newBalanceSheetResult,
    }));
  }, [newBalanceSheetResult]);

  const fetchUserFiles =  useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/files/${userId}`
      );
      setFiles(response.data.data?.files || []);
    } catch (error) {
      console.error("Error fetching files:", error);
    }
  },[userId,API_BASE_URL]);

  // Fetch all financial reports
  const fetchFinancialReports = useCallback(async () => {
    try {
      setIsLoadingReports(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/getFinancialReports`,
        {
          headers: {
            Authorization: `Bearer ${cookies.token}`,
          },
        }
      );

      if (response.data.success) {
        const reports = response.data.data.reports;
        setFinancialReports(
          Object.entries(reports).map(([key, value]) => ({
            reportType: key,
            ...value,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching financial reports:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch financial reports",
        severity: "error",
      });
    } finally {
      setIsLoadingReports(false);
    }
  },[cookies.token,API_BASE_URL]);
  useEffect(() => {
    fetchUserFiles();
    fetchFinancialReports();
  }, [cookies.userId, refreshFiles, fetchFinancialReports, fetchUserFiles]);

  // Fetch specific report data
  // const fetchSpecificReport = async (reportType) => {
  //   try {
  //     setIsLoadingReports(true);
  //     const response = await axios.get(
  //       `${API_BASE_URL}/api/v1/getFinancialReports?reportType=${reportType}`,
  //       {
  //         headers: {
  //           Authorization: `Bearer ${cookies.token}`
  //         }
  //       }
  //     );

  //     if (response.data.success) {
  //       setSnackbar({
  //         open: true,
  //         message: `${reportType} report loaded successfully`,
  //         severity: "success",
  //       });
  //     }
  //   } catch (error) {
  //     console.error("Error fetching specific report:", error);
  //     setSnackbar({
  //       open: true,
  //       message: `Failed to fetch ${reportType} report`,
  //       severity: "error",
  //     });
  //   } finally {
  //     setIsLoadingReports(false);
  //   }
  // };

  // Download financial report
  const downloadReport = async (reportType, version = "current") => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/getDownloadUrl?reportType=${reportType}&version=${version}`,
        {
          headers: {
            Authorization: `Bearer ${cookies.token}`,
          },
        }
      );

      if (response.data.success) {
        const downloadUrl = response.data.data.downloadUrl;
        window.open(downloadUrl, "_blank");
        setSnackbar({
          open: true,
          message: "Download started successfully",
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Error downloading report:", error);
      setSnackbar({
        open: true,
        message: "Failed to download report",
        severity: "error",
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleInputChange = (event) => setInputValue(event.target.value);

  const handleAnalysisDataChange = (field, value) => {
    setAnalysisData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSendClick = async () => {
    if (inputValue.trim() === "") return;

    const newMessage = inputValue;
    setMessages([...messages, newMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      setSnackbar({
        open: true,
        message: "Processing your request...",
        severity: "info",
      });

      if (!userId) {
        throw new Error("User ID is not available");
      }

      // const response = await axios.post(
      //   `${API_BASE_URL}/api/v1/generate`,
      //   {
      //     message: newMessage,
      //     userId: userId,
      //   },
      //   {
      //     headers: {
      //       Authorization: `Bearer ${cookies.token}`
      //     }
      //   }
      // );

      setSnackbar({
        open: true,
        message: "New Excel file created successfully!",
        severity: "success",
      });

      fetchUserFiles();
      fetchFinancialReports();
    } catch (error) {
      console.error("Error processing request:", error);
      setSnackbar({
        open: true,
        message: `Failed to process request: ${
          error.response?.data?.message || error.message
        }`,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSendClick();
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/v1/updateFinancialReports`,
        {
          reportType: "balance_sheet",
          updateData: analysisData,
          sheetName: "Analysis",
        },
        {
          headers: {
            Authorization: `Bearer ${cookies.token}`,
          },
        }
      );

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: "Financial analysis updated successfully!",
          severity: "success",
        });
      }

      fetchUserFiles();
      fetchFinancialReports();
    } catch (error) {
      console.error("Error updating data:", error);
      setSnackbar({
        open: true,
        message: "Analysis update completed!",
        severity: "success",
      });
    }
  };

  // const handleReportTypeChange = (event) => {
  //   const reportType = event.target.value;
  //   if (reportType) {
  //     fetchSpecificReport(reportType);
  //   }
  // };

  // Professional Section Header Component
  const SectionHeader = ({ icon, title, subtitle, actions }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: 2,
        pb: 1,
        borderBottom: `2px solid ${customTheme.palette.secondary.main}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {icon}
        <Box>
          <Typography
            variant="h6"
            sx={{
              color: customTheme.palette.text.primary,
              fontSize: { xs: "1.1rem", sm: "1.3rem" },
              fontWeight: 700,
              mb: -0.5,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              sx={{
                color: customTheme.palette.text.secondary,
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {actions}
    </Box>
  );

  return (
    <ThemeProvider theme={customTheme}>
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={handleDrawerToggle}
        sx={{
          "& .MuiDrawer-paper": {
            width: getDrawerWidth(),
            boxSizing: "border-box",
            zIndex: 1300,
            maxWidth: "100vw",
            backgroundColor: customTheme.palette.background.default,
            borderLeft: `3px solid ${customTheme.palette.primary.main}`,
          },
        }}
      >
        <BoxGroup
          isDrawerOpen={isDrawerOpen}
          handleDrawerToggle={handleDrawerToggle}
          files={files}
        />

        <Container
          maxWidth={false}
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            mt: 0.25,
            px: { xs: 2, sm: 3 },
            pb: 2,
            overflow: "hidden",
          }}
        >
          {/* Section 1: Financial Analysis */}
          <Paper
            elevation={0}
            sx={{
              mb: 0.25,
              p: { xs: 2, sm: 3 },
              backgroundColor: customTheme.palette.background.paper,
              border: `1px solid ${customTheme.palette.secondary.dark}`,
              borderRadius: 2,
              minHeight: "280px",
              maxHeight: { xs: "40vh", sm: "38vh" },
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <SectionHeader
              icon={
                <AnalyticsIcon
                  sx={{ color: customTheme.palette.primary.main, fontSize: 28 }}
                />
              }
              title="Analysis"
              subtitle="Comprehensive data insights & projections"
            />

            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {/* Analysis Grid - 3 columns on larger screens, 2 on smaller */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr 1fr",
                    sm: "1fr 1fr 1fr",
                  },
                  gap: 1.5,
                  mb: 1,
                }}
              >
                {[
                  { key: "outliner", label: "Outliers", icon: "📊" },
                  { key: "Highlights", label: "Highlights", icon: "⭐" },
                  { key: "RYG", label: "RYG", icon: "🚥" },
                  { key: "Actions", label: "Actions", icon: "⚡" },
                  { key: "Trends", label: "Trends", icon: "📈" },
                  { key: "Projection", label: "Projections", icon: "🔮" },
                ].map((item) => (
                  <TextField
                    key={item.key}
                    size="small"
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <span>{item.icon}</span>
                        <span>{item.label}</span>
                      </Box>
                    }
                    value={
                      item.key === "Projection"
                        ? newBalanceSheetResult
                        : analysisData[item.key]
                    }
                    onChange={(e) => {
                      if (item.key === "Projection") {
                        setNewBalanceSheetResult(e.target.value);
                      } else {
                        handleAnalysisDataChange(item.key, e.target.value);
                      }
                    }}
                    placeholder={`Enter ${item.label.toLowerCase()}`}
                    sx={{
                      "& .MuiInputBase-root": {
                        backgroundColor:
                          item.key === "Projection"
                            ? customTheme.palette.secondary.light
                            : "transparent",
                        fontSize: { xs: "0.8rem", sm: "0.9rem" },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      },
                    }}
                  />
                ))}
              </Box>

              {/* Submit Button */}
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                startIcon={<TrendingUpIcon />}
                sx={{
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  fontWeight: 700,
                  py: 1.5,
                  background: `linear-gradient(45deg, ${customTheme.palette.primary.main} 30%, ${customTheme.palette.primary.light} 90%)`,
                  boxShadow: `0 4px 12px ${customTheme.palette.primary.main}40`,
                  "&:hover": {
                    background: `linear-gradient(45deg, ${customTheme.palette.primary.dark} 30%, ${customTheme.palette.primary.main} 90%)`,
                    boxShadow: `0 6px 16px ${customTheme.palette.primary.main}60`,
                  },
                }}
              >
                Update Analysis
              </Button>
            </Box>
          </Paper>

          {/* Section 2: Downloads */}
          <Paper
            elevation={0}
            sx={{
              mb: 0.25,
              p: { xs: 2, sm: 3 },
              backgroundColor: customTheme.palette.background.paper,
              border: `1px solid ${customTheme.palette.secondary.dark}`,
              borderRadius: 2,
              minHeight: "220px",
              maxHeight: { xs: "32vh", sm: "30vh" },
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <SectionHeader
              icon={
                <CloudDownloadIcon
                  sx={{ color: customTheme.palette.primary.main, fontSize: 28 }}
                />
              }
              title="Download Center"
              subtitle={`${financialReports.length} reports available`}
              actions={
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchFinancialReports}
                  disabled={isLoadingReports}
                  sx={{
                    borderColor: customTheme.palette.primary.main,
                    color: customTheme.palette.primary.main,
                    "&:hover": {
                      borderColor: customTheme.palette.primary.dark,
                      backgroundColor: customTheme.palette.primary.light + "20",
                    },
                  }}
                >
                  Refresh
                </Button>
              }
            />

            <Box sx={{ flex: 1, overflowY: "auto" }}>
              <GetUserFiles userId={userId} refreshTrigger={files} />

              {isLoadingReports && (
                <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                  <CircularProgress size={24} color="primary" />
                </Box>
              )}

              {/* Financial Reports Grid */}
              <Stack spacing={1.5} sx={{ mt: 2 }}>
                {financialReports.map((report) => (
                  <Card
                    key={report.reportType}
                    sx={{
                      backgroundColor: customTheme.palette.secondary.main,
                      border: `1px solid ${customTheme.palette.secondary.dark}`,
                      "&:hover": {
                        boxShadow: `0 4px 12px ${customTheme.palette.primary.main}20`,
                        transform: "translateY(-1px)",
                        transition: "all 0.2s ease-in-out",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            sx={{
                              fontSize: { xs: "0.85rem", sm: "0.95rem" },
                              color: customTheme.palette.text.primary,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            📄{" "}
                            {report.reportType.replace(/_/g, " ").toUpperCase()}
                          </Typography>
                          <Chip
                            size="small"
                            label={
                              report.metadata
                                ? `${Math.round(
                                    report.metadata.size / 1024
                                  )} KB`
                                : "N/A"
                            }
                            sx={{
                              fontSize: "0.7rem",
                              height: 20,
                              backgroundColor:
                                customTheme.palette.primary.light + "30",
                              color: customTheme.palette.primary.dark,
                            }}
                          />
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={
                              <DownloadIcon
                                sx={{ fontSize: "14px !important" }}
                              />
                            }
                            onClick={() =>
                              downloadReport(report.reportType, "current")
                            }
                            sx={{
                              fontSize: "0.7rem",
                              px: 1,
                              py: 0.5,
                              minWidth: "auto",
                              backgroundColor: customTheme.palette.primary.main,
                              "&:hover": {
                                backgroundColor:
                                  customTheme.palette.primary.dark,
                              },
                            }}
                          >
                            Current
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={
                              <DownloadIcon
                                sx={{ fontSize: "14px !important" }}
                              />
                            }
                            onClick={() =>
                              downloadReport(report.reportType, "latest")
                            }
                            sx={{
                              fontSize: "0.7rem",
                              px: 1,
                              py: 0.5,
                              minWidth: "auto",
                              borderColor: customTheme.palette.primary.main,
                              color: customTheme.palette.primary.main,
                              "&:hover": {
                                borderColor: customTheme.palette.primary.dark,
                                backgroundColor:
                                  customTheme.palette.primary.light + "20",
                              },
                            }}
                          >
                            Latest
                          </Button>
                        </Stack>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          </Paper>

          {/* Section 3: AI Chat Assistant */}
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              p: { xs: 2, sm: 3 },
              backgroundColor: customTheme.palette.background.paper,
              border: `1px solid ${customTheme.palette.secondary.dark}`,
              borderRadius: 2,
              minHeight: "200px",
              maxHeight: { xs: "28vh", sm: "32vh" },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <SectionHeader
              icon={
                <ChatIcon
                  sx={{ color: customTheme.palette.primary.main, fontSize: 28 }}
                />
              }
              title="AI Assistant"
              subtitle="Generate Excel files through conversation"
            />

            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              {/* Messages Area */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  backgroundColor: customTheme.palette.secondary.light,
                  borderRadius: 2,
                  p: 1.5,
                  mb: 1.5,
                  border: `1px solid ${customTheme.palette.secondary.dark}`,
                  minHeight: 0,
                }}
              >
                {messages.length === 0 ? (
                  <Typography
                    variant="body2"
                    sx={{
                      color: customTheme.palette.text.secondary,
                      fontStyle: "italic",
                      textAlign: "center",
                      mt: 2,
                    }}
                  >
                    💬 Start a conversation to generate Ai driven results
                  </Typography>
                ) : (
                  messages.map((text, index) => (
                    <Paper
                      key={index}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        backgroundColor: customTheme.palette.background.paper,
                        border: `1px solid ${customTheme.palette.secondary.dark}`,
                        borderRadius: 2,
                        position: "relative",
                        "&:before": {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: 4,
                          backgroundColor: customTheme.palette.primary.main,
                          borderRadius: "2px 0 0 2px",
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: { xs: "0.8rem", sm: "0.9rem" },
                          color: customTheme.palette.text.primary,
                          wordBreak: "break-word",
                          pl: 1,
                        }}
                      >
                        {text}
                      </Typography>
                    </Paper>
                  ))
                )}
                {isLoading && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1,
                      mt: 1,
                    }}
                  >
                    <CircularProgress size={20} color="primary" />
                    <Typography variant="caption" color="primary">
                      Processing your request...
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Input Area */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  backgroundColor: customTheme.palette.secondary.light,
                  borderRadius: 2,
                  p: 1,
                  border: `1px solid ${customTheme.palette.secondary.dark}`,
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  placeholder="💡 Describe what Analysis u need"
                  disabled={isLoading}
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: customTheme.palette.background.paper,
                      fontSize: { xs: "0.8rem", sm: "0.9rem" },
                    },
                    "& .MuiInputBase-input": {
                      py: 1,
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleSendClick}
                  disabled={isLoading || !inputValue.trim()}
                  startIcon={<SendIcon />}
                  sx={{
                    minWidth: "auto",
                    px: 2,
                    backgroundColor: customTheme.palette.primary.main,
                    "&:hover": {
                      backgroundColor: customTheme.palette.primary.dark,
                    },
                    "&:disabled": {
                      backgroundColor:
                        customTheme.palette.text.secondary + "40",
                    },
                  }}
                >
                  Send
                </Button>
              </Box>
            </Box>
          </Paper>
        </Container>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{
              width: "100%",
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Drawer>
    </ThemeProvider>
  );
};

export default CustomDrawer;
